/**
 * CustomerTracking — portal-flavoured tracking lookup.
 *
 * Reuses the existing public tracking machinery (`useTrackingQuery`,
 * `TrackingResultPanel`) inside the portal chrome rather than duplicating the
 * S10 validation and React-Query plumbing. Accepts the tracking number from
 * the `?tn=…` query string so deep-links from the dashboard / shipments page
 * work out of the box.
 *
 * Note: service-mail's `GET /v1/track/:tn` is `@Public()` — no bearer is
 * required even though the citizen is signed in. The portal context just
 * gives them a chrome to come back to.
 */
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';

import { TrackingResultPanel } from '../track/TrackingResultPanel';
import {
  isValidTrackingNumber,
  normaliseTrackingNumber,
} from '../track/upu-s10';
import { PageHeader } from './PortalUI';

export function CustomerTracking(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const tnParam = params.get('tn') ?? '';
  const [input, setInput] = useState<string>(tnParam);
  const [submitted, setSubmitted] = useState<string>(tnParam);

  // Sync local state when the deep-linked param changes (back / forward nav).
  useEffect(() => {
    setInput(tnParam);
    setSubmitted(tnParam);
  }, [tnParam]);

  const normalisedInput = normaliseTrackingNumber(input);
  const inputValid = isValidTrackingNumber(normalisedInput);
  const normalisedSubmitted = normaliseTrackingNumber(submitted);
  const showResult = isValidTrackingNumber(normalisedSubmitted);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!inputValid) {
      return;
    }
    setSubmitted(normalisedInput);
    setParams({ tn: normalisedInput }, { replace: false });
  }

  return (
    <div>
      <PageHeader
        title="Track a Package"
        description="Enter your tracking number to see real-time status."
      />

      <div className="bg-white rounded-xl border border-border p-6 mb-6">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto" noValidate>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted" />
              <input
                type="text"
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                placeholder="e.g. EE123456785NG"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                aria-label="Tracking number"
                className="w-full pl-12 pr-4 py-3.5 text-base font-mono uppercase border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
            </div>
            <button
              type="submit"
              disabled={!inputValid}
              className="px-8 py-3.5 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              Track
            </button>
          </div>
          {input.length > 0 && !inputValid ? (
            <p className="mt-2 text-xs text-rose-700">
              That doesn&apos;t look like a valid 13-character UPU S10 number.
            </p>
          ) : null}
        </form>
      </div>

      {showResult ? (
        <TrackingResultPanel trackingNumber={normalisedSubmitted} />
      ) : (
        <div className="bg-white rounded-xl border border-border p-12 text-center">
          <Search className="w-10 h-10 text-muted mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-700 mb-1">
            Enter a tracking number to begin
          </p>
          <p className="text-xs text-muted">
            We&apos;ll show the registered milestones and current status.
          </p>
        </div>
      )}
    </div>
  );
}
